import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '番号吧',
    url: 'https://miaoxiaobao.cn',
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于番号吧 - 专业番号查询平台',
    description: '了解番号吧的品牌故事、核心优势、用户口碑及联系方式。',
    url: 'https://miaoxiaobao.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '番号吧',
    },
  };

  usePageMeta({
    title: '关于我们 - 番号吧',
    description: '了解番号吧的品牌故事、核心优势、用户口碑及联系方式。专业番号查询平台，海量番号信息检索服务。',
    keywords: '番号吧介绍,番号查询平台,关于番号吧,联系方式',
    canonicalUrl: 'https://miaoxiaobao.cn/about',
    jsonLd: [orgSchema, faqSchema, webpageSchema],
  });

  return (
    <main>
      <Breadcrumb items={[{ label: '关于我们' }]} />

      <section className="w-full px-4 md:px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
              关于番号吧
            </h1>
            <p className="text-foreground-500 max-w-lg mx-auto">
              专业番号查询与影片信息平台，致力于为番号爱好者提供最便捷的信息检索服务
            </p>
          </div>

          <div className="mb-14">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-4">
              品牌故事
            </h2>
            <div className="text-foreground-600 leading-relaxed space-y-3">
              <p>
                番号吧创立于{siteInfo.founded}年，是国内领先的专业番号查询与影片信息平台。我们致力于打造最全面、最准确的番号信息数据库，为番号爱好者提供一站式的信息检索服务。
              </p>
              <p>
                在番号信息碎片化的时代，番号吧以"精准检索、全面覆盖"为核心理念，通过技术驱动不断优化搜索体验。从番号编码到演员资料，从厂商信息到分类标签，我们力求每一个数据都准确可靠。
              </p>
              <p>
                经过持续发展，番号吧已收录超过<strong>{(siteInfo.stats.totalArticles / 10000).toFixed(0)}万</strong>条番号信息，覆盖<strong>{siteInfo.stats.totalCategories}</strong>个分类体系，收录演员超过<strong>{(siteInfo.stats.totalActors / 10000).toFixed(0)}万</strong>名，日均服务用户超过<strong>{(siteInfo.stats.dailyVisitors / 10000).toFixed(0)}万</strong>人次。这些数字的背后，是我们对品质的坚持与对用户需求的深刻理解。
              </p>
            </div>
          </div>

          <div className="mb-14">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {siteInfo.features.map((feature) => (
                <div
                  key={feature.title}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                    <i className={`${feature.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-14">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {siteInfo.testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-14">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {siteInfo.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-4">
              联系我们
            </h2>
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900 mb-1">
                    电子邮件
                  </p>
                  <p className="text-sm text-foreground-600">
                    无论是功能建议、数据反馈还是合作咨询，欢迎随时来信：<strong>{siteInfo.email}</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}